import React, { useEffect, useRef, useState } from 'react';
import { style as imageStyle, url as imageUrl } from 'utils/image';

import './exhibitor-card.scss';

interface ExhibitorInfo {
  id: number;
  name: string;
  short_name?: string;
  logo?: string;
  exhibitor_logo?: string;
  booth?: string;
  description?: string;
  contact_person?: string;
  contact_person_mobile?: string;
  banner?: string;
  [key: string]: any;
}

interface ExhibitorConfig {
  banner?: string;
  defaultBanner?: string;
  search?: boolean;
  showList?: boolean;
  autoShowAll?: boolean;
  list?: any[];
  exhibitorOrder?: number[];
}

interface GalleryConfigDetail {
  default_exhibitor_banner?: string;
  [key: string]: any;
}

const DEFAULT_EXHIBITOR_BANNER_FALLBACK =
  'attachment/6b1f910c2d5a73f71d7e103f0aacb1e5.jpg';

const parseWindowJSON = (key: string) => {
  const val = (window as any)[key];
  if (!val) return null;
  if (typeof val === 'string') {
    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  }
  return val;
};

const ExhibitorCard: React.FC<any> = () => {
  const exhibitorInfo: ExhibitorInfo | null = parseWindowJSON('exhibitorInfo');
  const exhibitorConfig: ExhibitorConfig =
    parseWindowJSON('exhibitorConfig') || {};
  const configDetail: GalleryConfigDetail = parseWindowJSON('configDetail') || {};

  const {
    name,
    exhibitor_logo,
    logo,
    booth,
    desc,
    contact_person_mobile,
  } = exhibitorInfo || ({} as ExhibitorInfo);

  const boothRef = useRef<HTMLDivElement>(null);
  const [boothExpanded, setBoothExpanded] = useState(false);
  const [boothOverflow, setBoothOverflow] = useState(false);

  useEffect(() => {
    setBoothExpanded(false);
  }, [booth]);

  useEffect(() => {
    const boothElement = boothRef.current;
    if (!boothElement) {
      setBoothOverflow(false);
      return;
    }

    setBoothOverflow(boothElement.scrollHeight > boothElement.clientHeight + 1);
  }, [booth, boothExpanded]);

  if (!exhibitorInfo) return null;

  // banner 回退顺序：展商单独头图 -> 相册全局默认头图 -> 后端注入默认头图 -> 固定兜底图
  const bannerKey =
    exhibitorInfo.gallery_pagedata_banner ||
    configDetail.default_exhibitor_banner ||
    exhibitorConfig.defaultBanner ||
    DEFAULT_EXHIBITOR_BANNER_FALLBACK;
  const bannerSrc = bannerKey ? imageStyle(bannerKey, 'limit', { w: 750 }) : '';
  const logoKey = [exhibitor_logo, logo].find(
    (item) => typeof item === 'string' && item.trim()
  ) || '';
  const logoSrc = logoKey
    ? imageUrl(logoKey, {
        'x-oss-process':
          'image/resize,m_pad,w_400,h_400,color_ffffff/format,jpg',
      }, 1)
    : '';

  const themeMainColor =
    (configDetail && configDetail.theme && configDetail.theme.main) || '#24d9cd';

  const handleContact = () => {
    if (contact_person_mobile) {
      window.location.href = `tel:${contact_person_mobile}`;
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    }
  };

  return (
    <>
      <div className="exhibitor-card-component">
        {/* Banner 区域 */}
        <div className="exhibitor-banner">
          {bannerSrc ? (
            <img src={bannerSrc} alt="" />
          ) : (
            <div className="banner-placeholder" />
          )}
        </div>

        {/* 信息卡片 */}
        <div className="exhibitor-info-card">
          {logoSrc ? (
            <div className="exhibitor-logo">
              <img src={logoSrc} alt={name} className="caowei" />
            </div>
          ) : null}
          <div className="exhibitor-card-main">
            <div className="exhibitor-card-name">{name}</div>
            {booth && (
              <div className="exhibitor-card-booth-block">
                <div className="exhibitor-card-booth-label">展位号</div>
                <div
                  ref={boothRef}
                  className={
                    boothExpanded
                      ? 'exhibitor-card-booth expanded'
                      : 'exhibitor-card-booth'
                  }
                >
                  {booth}
                </div>
                {boothOverflow && !boothExpanded ? (
                  <button
                    type="button"
                    className="exhibitor-card-booth-expand"
                    style={{ color: themeMainColor }}
                    onClick={() => setBoothExpanded(true)}
                  >
                    展开全部展位
                  </button>
                ) : null}
              </div>
            )}
            {desc && <div className="exhibitor-card-desc">{desc}</div>}
            {contact_person_mobile && (
              <div
                className="exhibitor-card-contact"
                style={{ color: themeMainColor }}
                onClick={handleContact}
              >
                <span className="contact-icon">📞</span>
                <span>联系展商</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 返回按钮 */}
      <div className="exhibitor-back-btn" onClick={handleBack}>
        <span className="back-icon">‹</span>
        <span>返回展商列表</span>
      </div>
    </>
  );
};

export default ExhibitorCard;
